import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, collection, addDoc, serverTimestamp, runTransaction, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase';
import { Send, Clock, UserCircle, Menu } from 'lucide-react';
import Header from './components/Header';
import Balance from './components/Balance';
import Actions from './components/Actions';
import TransactionHistory from './components/TransactionHistory';
import Profile from './components/Profile';
import Settings from './components/Settings';
import SignIn from './components/SignIn';

interface User {
  uid: string;
  email: string | null;
}

function App() {
  const [balance, setBalance] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser ? { uid: currentUser.uid, email: currentUser.email } : null);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user?.uid) {
      const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (doc) => {
        if (doc.exists()) {
          setBalance(doc.data()?.balance || 0);
        }
      });
      return () => unsubscribe();
    }
  }, [user]);

  const handleSendMoney = async (amount: number, recipientEmail: string) => {
    if (!user?.uid || !user.email) return;
    
    setError(null);
    try {
      const recipientQuery = query(collection(db, 'users'), where('email', '==', recipientEmail));
      const recipientSnapshot = await getDocs(recipientQuery);
      
      if (recipientSnapshot.empty) {
        throw new Error('Recipient not found');
      }

      const recipientDoc = recipientSnapshot.docs[0];
      const recipientId = recipientDoc.id;
      const timestamp = serverTimestamp();

      await addDoc(collection(db, 'transactions'), {
        type: 'send',
        amount,
        userId: user.uid,
        senderEmail: user.email,
        recipientEmail,
        timestamp,
        status: 'completed'
      });

      await addDoc(collection(db, 'transactions'), {
        type: 'receive',
        amount,
        userId: recipientId,
        senderEmail: user.email,
        recipientEmail,
        timestamp,
        status: 'completed'
      });

      await runTransaction(db, async (transaction) => {
        const senderDoc = await transaction.get(doc(db, 'users', user.uid));
        const recipientDoc = await transaction.get(doc(db, 'users', recipientId));

        if (!senderDoc.exists() || !recipientDoc.exists()) {
          throw new Error('User document not found');
        }

        const senderBalance = senderDoc.data().balance || 0;
        if (senderBalance < amount) {
          throw new Error('Insufficient funds');
        }

        transaction.update(doc(db, 'users', user.uid), {
          balance: senderBalance - amount
        });

        transaction.update(doc(db, 'users', recipientId), {
          balance: (recipientDoc.data().balance || 0) + amount
        });
      });

    } catch (err) {
      console.error('Error sending money:', err);
      setError(err instanceof Error ? err.message : 'Failed to send money. Please try again.');
    }
  };

  const handleRequestMoney = async (amount: number, fromEmail: string) => {
    if (!user?.uid || !user.email) return;
    
    setError(null);
    try {
      const fromUserQuery = query(collection(db, 'users'), where('email', '==', fromEmail));
      const fromUserSnapshot = await getDocs(fromUserQuery);
      
      if (fromUserSnapshot.empty) {
        throw new Error('User not found');
      }

      const fromUserId = fromUserSnapshot.docs[0].id;

      await addDoc(collection(db, 'moneyRequests'), {
        amount,
        requesterId: user.uid,
        requesterEmail: user.email,
        fromUserId,
        fromEmail,
        status: 'pending',
        timestamp: serverTimestamp()
      });

    } catch (err) {
      console.error('Error requesting money:', err);
      setError(err instanceof Error ? err.message : 'Failed to request money. Please try again.');
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'home':
        return (
          <>
            <Balance balance={balance} />
            {error && (
              <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
                {error}
              </div>
            )}
            <Actions 
              onSend={handleSendMoney} 
              onRequest={handleRequestMoney} 
              currentUserEmail={user?.email || ''}
            />
            <TransactionHistory userId={user?.uid || ''} />
          </>
        );
      case 'activity':
        return <TransactionHistory userId={user?.uid || ''} />;
      case 'profile':
        return <Profile />;
      case 'settings':
        return <Settings />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-clkk-dark text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clkk-blue"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-clkk-dark text-white flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8 pb-24">
          <Routes>
            <Route 
              path="/signin" 
              element={user ? <Navigate to="/" replace /> : <SignIn />} 
            />
            <Route
              path="/"
              element={
                user ? (
                  <div className="space-y-6">
                    {renderContent()}
                  </div>
                ) : (
                  <Navigate to="/signin" replace />
                )
              }
            />
          </Routes>
        </main>
        {user && (
          <nav className="fixed bottom-0 left-0 right-0 bg-clkk-dark border-t border-clkk-gray-800 backdrop-blur-sm bg-opacity-90">
            <ul className="flex justify-around max-w-lg mx-auto">
              <li>
                <button 
                  onClick={() => setActiveSection('home')} 
                  className={`p-4 flex flex-col items-center min-w-[4rem] ${
                    activeSection === 'home' 
                      ? 'text-transparent bg-gradient-brand bg-clip-text' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  <Send size={24} />
                  <span className="text-xs mt-1">Home</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveSection('activity')} 
                  className={`p-4 flex flex-col items-center min-w-[4rem] ${
                    activeSection === 'activity' 
                      ? 'text-transparent bg-gradient-brand bg-clip-text' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  <Clock size={24} />
                  <span className="text-xs mt-1">Activity</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveSection('profile')} 
                  className={`p-4 flex flex-col items-center min-w-[4rem] ${
                    activeSection === 'profile' 
                      ? 'text-transparent bg-gradient-brand bg-clip-text' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  <UserCircle size={24} />
                  <span className="text-xs mt-1">Profile</span>
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveSection('settings')} 
                  className={`p-4 flex flex-col items-center min-w-[4rem] ${
                    activeSection === 'settings' 
                      ? 'text-transparent bg-gradient-brand bg-clip-text' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  <Menu size={24} />
                  <span className="text-xs mt-1">Settings</span>
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </Router>
  );
}

export default App;